/**
 * Property directory (migration 20260918000031): the registry of managed
 * hospitality properties with their Mission Control / UniFi / Auvik ids and
 * Learn links. Locations link to a row through
 * `locations.hierarchy->>'property_code'`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Json } from '@pazportal/supabase';
import { supabase } from '../lib/supabase';

export interface PropertyDirectoryRow {
  code: string;
  name: string;
  brand: string | null;
  city: string | null;
  state: string | null;
  lat: number | null;
  lng: number | null;
  rankings_url: string | null;
  mission_control_site_id: string | null;
  unifi_host_id: string | null;
  auvik_domain_prefix: string | null;
  learn_url: string | null;
  engineering_url: string | null;
  ordering_url: string | null;
  communications_vendor_url: string | null;
  brand_auvik_url: string | null;
  additional_networks: Json;
  aliases: string[];
  updated_at: string;
}

export interface LinkedLocation {
  id: string;
  name: string;
  customer_id: string;
  customer_name: string | null;
  property_code: string | null;
}

export interface PropertyDirectoryData {
  properties: PropertyDirectoryRow[];
  /** Locations grouped by property code. */
  locationsByCode: Map<string, LinkedLocation[]>;
  /** Active locations with no property code (candidates for relinking). */
  unlinkedLocations: LinkedLocation[];
}

export function propertyCodeOf(hierarchy: Json | null | undefined): string | null {
  if (!hierarchy || typeof hierarchy !== 'object' || Array.isArray(hierarchy)) return null;
  const code = (hierarchy as Record<string, unknown>).property_code;
  return typeof code === 'string' && code.trim() !== '' ? code : null;
}

export function usePropertyDirectory() {
  return useQuery({
    queryKey: ['property-directory'],
    queryFn: async (): Promise<PropertyDirectoryData> => {
      const [propsRes, locsRes] = await Promise.all([
        supabase
          .from('property_directory')
          .select(
            'code, name, brand, city, state, lat, lng, rankings_url, mission_control_site_id, unifi_host_id, auvik_domain_prefix, learn_url, engineering_url, ordering_url, communications_vendor_url, brand_auvik_url, additional_networks, aliases, updated_at',
          )
          .order('code'),
        supabase
          .from('locations')
          // location_uptime_samples also joins locations↔customers, so the FK
          // must be named or PostgREST reports the embed as ambiguous.
          .select('id, name, customer_id, hierarchy, customer:customers!locations_customer_id_fkey(id, name)')
          .is('deleted_at', null)
          .order('name'),
      ]);
      if (propsRes.error) throw propsRes.error;
      if (locsRes.error) throw locsRes.error;

      const locationsByCode = new Map<string, LinkedLocation[]>();
      const unlinkedLocations: LinkedLocation[] = [];
      for (const raw of locsRes.data ?? []) {
        const customer = raw.customer as unknown as { id: string; name: string } | null;
        const linked: LinkedLocation = {
          id: raw.id,
          name: raw.name,
          customer_id: raw.customer_id,
          customer_name: customer?.name ?? null,
          property_code: propertyCodeOf(raw.hierarchy),
        };
        if (linked.property_code) {
          const list = locationsByCode.get(linked.property_code) ?? [];
          list.push(linked);
          locationsByCode.set(linked.property_code, list);
        } else {
          unlinkedLocations.push(linked);
        }
      }

      return {
        properties: (propsRes.data ?? []) as unknown as PropertyDirectoryRow[],
        locationsByCode,
        unlinkedLocations,
      };
    },
    staleTime: 60 * 1000,
  });
}

export type PropertyDirectoryPatch = Partial<
  Pick<
    PropertyDirectoryRow,
    | 'name'
    | 'brand'
    | 'city'
    | 'state'
    | 'lat'
    | 'lng'
    | 'rankings_url'
    | 'mission_control_site_id'
    | 'unifi_host_id'
    | 'auvik_domain_prefix'
    | 'learn_url'
    | 'engineering_url'
    | 'ordering_url'
    | 'communications_vendor_url'
    | 'brand_auvik_url'
    | 'aliases'
  >
>;

export function useUpdatePropertyDirectory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ code, patch }: { code: string; patch: PropertyDirectoryPatch }) => {
      const { error } = await supabase.from('property_directory').update(patch).eq('code', code);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['property-directory'] });
      qc.invalidateQueries({ queryKey: ['unifi-sites'] });
    },
  });
}
